/**
 * Marketing statistics displayed across the site.
 *
 * These are MILESTONE figures, not live telemetry — they change when we cross a
 * round number worth announcing, not as the catalog grows. Update them here and
 * nowhere else.
 *
 * NOT for press releases. Files under client/public/press/ are dated historical
 * records and must keep the figures that were accurate on their publish date.
 */
const LATENCY_MS = 400;

/** Slab-only mode: reads the slab label and skips card identification. */
const SLAB_ONLY_LATENCY_MS = 100;

/**
 * Catalog size in whole millions — the ONE number to change at a milestone.
 * Both `catalogCount` ("13M+") and `catalogWords` ("Thirteen million") derive
 * from it, so the numeral and the spelled-out form cannot disagree.
 */
const CATALOG_MILLIONS = 16;

/**
 * Spelled-out millions, capitalised for sentence-initial use — which is how the
 * marketing copy uses it ("Thirteen million cards. Ninety years of history.").
 *
 * A lookup rather than a general number-to-words routine: milestones only ever
 * land on whole millions, so this is the whole domain. Covers 10–30 with no
 * gaps — a partial table would silently ship "22 million" prose beside "22M+"
 * numerals. Extend it when the catalog passes 30M. If a value is missing,
 * `catalogWords` falls back to the numeral ("31 million"), which is still
 * correct English — never "undefined million".
 */
const MILLIONS_IN_WORDS: Record<number, string> = {
  10: "Ten",
  11: "Eleven",
  12: "Twelve",
  13: "Thirteen",
  14: "Fourteen",
  15: "Fifteen",
  16: "Sixteen",
  17: "Seventeen",
  18: "Eighteen",
  19: "Nineteen",
  20: "Twenty",
  21: "Twenty-one",
  22: "Twenty-two",
  23: "Twenty-three",
  24: "Twenty-four",
  25: "Twenty-five",
  26: "Twenty-six",
  27: "Twenty-seven",
  28: "Twenty-eight",
  29: "Twenty-nine",
  30: "Thirty",
};

export const STATS = {
  /** Total cards in the catalog, numeral form. The "+" means "more than". */
  catalogCount: `${CATALOG_MILLIONS}M+`,
  /**
   * Same figure spelled out and capitalised, for prose that reads better in
   * words. Note it carries no "+" — "Thirteen million cards" is the natural
   * phrasing, matching how this copy has always read.
   */
  catalogWords: `${MILLIONS_IN_WORDS[CATALOG_MILLIONS] ?? CATALOG_MILLIONS} million`,
  /** Mean average precision of visual identification. */
  accuracy: "99.5%",
  /** Common latency form: "400ms". Prose supplies "under" / "sub-". */
  latency: `${LATENCY_MS}ms`,
  /** Raw milliseconds, for prose like "under 300 milliseconds". */
  latencyMs: LATENCY_MS,
  /** Slab-only mode latency: "100ms". Prose supplies "under" / "sub-". */
  slabOnlyLatency: `${SLAB_ONLY_LATENCY_MS}ms`,
  /** Raw milliseconds for slab-only mode, for prose like "under 100 milliseconds". */
  slabOnlyLatencyMs: SLAB_ONLY_LATENCY_MS,
  /** Free-tier monthly API call allowance. */
  freeTierCalls: 750,
  /** Parallel patterns, colors, and designs covered by the parallel identification beta (baseball). */
  parallelPatterns: "17,000+",
  /** Distinct sets the identification pipeline recognizes ("Sets Recognized"). */
  setsRecognized: "10,000+",
  /** Per-sport breakdown. Sums to less than catalogCount — TCGs make up the rest. */
  sports: {
    baseball: "5.1M+",
    football: "4M+",
    basketball: "3M+",
    hockey: "1.2M+",
  },
} as const;
